import { useState, useMemo } from 'react';

/**
 * Custom hook for grouping records by time periods and managing collapsed state
 * Used across Maintenance, Fines, Salik, BatterySwaps, and Insurance pages
 * 
 * @param {Array} records - Array of records to group
 * @param {string} dateField - Field name for the date (e.g., 'maintenance_date', 'fine_date', etc.)
 * @returns {Object} - Object containing grouped records, toggle function, and render helper
 */
export const useGroupedRecords = (records, dateField) => {
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Group records by time periods
  const groupRecordsByPeriod = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - 7);
    
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    
    const groups = {
      today: [],
      yesterday: [],
      lastWeek: [],
      thisMonth: [],
      lastMonth: [],
      older: []
    };
    
    records.forEach(record => {
      const dateValue = record[dateField];
      if (!dateValue) {
        groups.older.push(record);
        return;
      }
      
      const recordDate = new Date(dateValue);
      const recordDay = new Date(recordDate.getFullYear(), recordDate.getMonth(), recordDate.getDate());
      
      if (recordDay.getTime() === today.getTime()) {
        groups.today.push(record);
      } else if (recordDay.getTime() === yesterday.getTime()) {
        groups.yesterday.push(record);
      } else if (recordDay >= startOfWeek && recordDay < yesterday) {
        groups.lastWeek.push(record);
      } else if (recordDate >= startOfMonth && recordDate < today) {
        groups.thisMonth.push(record);
      } else if (recordDate >= startOfLastMonth && recordDate <= endOfLastMonth) {
        groups.lastMonth.push(record);
      } else {
        groups.older.push(record);
      }
    });
    
    return groups;
  }, [records, dateField]);

  // Toggle group collapsed state
  const toggleGroup = (groupName) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Check if a group is collapsed
  const isGroupCollapsed = (groupName) => {
    return collapsedGroups[groupName];
  };

  return {
    groupedRecords: groupRecordsByPeriod,
    toggleGroup,
    isGroupCollapsed,
    collapsedGroups
  };
};

// Helper function to get group title labels
export const getGroupLabels = () => ({
  today: 'Today',
  yesterday: 'Yesterday',
  lastWeek: 'Last Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  older: 'Older'
});

// Helper function to get group order for rendering
export const getGroupOrder = () => [
  'today',
  'yesterday', 
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'older'
];
